import { computed, inject } from '@angular/core';
import {
    addEntity,
    removeAllEntities,
    SelectEntityId,
    setEntities,
    setEntity,
    withEntities,
} from '@ngrx/signals/entities';
import { ICreateUserData, IUser } from '@app/core/data-models/user/user.model';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { UsersService } from '@app/core/services/backend/mci/users.service';
import { firstValueFrom } from 'rxjs';
import { UserBulkAction, UserStatus, userBulkAction, userStatusValue } from '@app/core/data-models/types/user.type';
import { UtilsService } from '@app/core/services/backend/mci/utils.service';
import { LogPriority } from '@app/core/services/log/log-priority.enum';
import { AppLog } from '@app/core/services/log/app-log.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { Sort } from '@angular/material/sort';
import { xor } from 'lodash';
import { AppPermissions } from '@app/core/services/acl/config/acl-permissions-constants';
import { Store } from '@ngrx/store';
import { showErrorMessage, showSuccessMessage } from '@app/store/actions';

const selectId: SelectEntityId<IUser> = (user) => user.userInfo.userName;

export interface IUserDetailsData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: UserStatus;
}

export interface IUserSettingsData {
    roles: string[];
    nvps: {
        TimeZone: string;
        EmployeeID: string;
    };
}

export type IUserFilterParams = {
    status?: UserStatus;
    searchTerm?: string;
    roles?: string[];
};

type UsersState = {
    filter: IUserFilterParams;
    selectedUserIds: string[];
    sort: Sort | undefined;
};

const initialState: UsersState = {
    filter: {},
    selectedUserIds: [],
    sort: undefined,
};

export const UsersStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),

    /**
     * User entities
     */
    withEntities<IUser>(),

    /**
     * User computed
     */
    withComputed((store) => ({
        isUserLoaded: computed<boolean>(() => store.entities().length > 0),

        allSystemRoles: computed<string[]>(() => {
            const roles = new Set<string>();
            store.entities().forEach((user) => {
                user.roles.forEach((role) => {
                    if (role.name) roles.add(role.name);
                });
            });
            return Array.from(roles);
        }),

        allSelectedUsers: computed<IUser[]>(() => store.selectedUserIds().map((id) => store.entityMap()[id])),

        usersFiltered: computed<IUser[]>(() => {
            const { status, roles, searchTerm } = store.filter();
            const sort = store.sort();

            let filteredUsers = store.entities().filter((user: IUser) => {
                let matchesStatus = true;
                let matchesRole = true;
                let matchesSearchTerm = true;

                if (status) {
                    matchesStatus = (user.userInfo.status as string).toLowerCase() === status.toLowerCase();
                }

                if (roles && roles.length > 0) {
                    matchesRole = user.roles.some((userRole) =>
                        userRole.code ? roles.includes(userRole.code) : false,
                    );
                }

                if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase();
                    matchesSearchTerm =
                        user.userInfo.userName.toLowerCase().includes(searchLower) ||
                        user.userInfo.firstName.toLowerCase().includes(searchLower) ||
                        user.userInfo.lastName.toLowerCase().includes(searchLower) ||
                        (user.userInfo.employeeId && user.userInfo.employeeId.includes(searchLower)) ||
                        user.roles.some((role) => role.name?.toLowerCase().includes(searchLower)) ||
                        user.nvps.some((nvp) => nvp.value.toLowerCase().includes(searchLower));
                }

                return matchesStatus && matchesRole && matchesSearchTerm;
            });

            if (sort) {
                filteredUsers = UtilsService.sortData<IUser>(sort, [...filteredUsers]);
            }

            return filteredUsers;
        }),
    })),

    /**
     * User props
     */
    withProps(({ isUserLoaded }) => ({
        isLoaded$: toObservable(isUserLoaded),
    })),

    /**
     * User methods
     */
    withMethods((store, usersService = inject(UsersService), logger = inject(AppLog), ngrxStore = inject(Store)) => ({
        /**
         * Load users from the API
         * @returns Promise<IUser[]>
         */
        async loadUsers(): Promise<IUser[]> {
            try {
                this.clearSelectedUsers();
                const users: IUser[] = await firstValueFrom(usersService.loadUsers());
                patchState(store, setEntities(users, { selectId }));
                return users;
            } catch (error) {
                logger.error('Failed to load users', { logPriority: LogPriority.HIGH }, error);
                this.removeAllEmptyEntities();
                return [];
            }
        },

        /**
         * Create a new user
         * @param userData The user data to create
         * @returns Promise<IUser>
         * @throws {HttpErrorResponse} Rethrows the HTTP error from the API call if user creation fails.
         *         The error contains `error.errors[0].message` with the server's error message.
         */
        async create(userData: ICreateUserData): Promise<IUser> {
            try {
                const newUser = await firstValueFrom(usersService.createUser(userData));
                if (store.isUserLoaded()) {
                    patchState(store, addEntity(newUser, { selectId }));
                } else {
                    this.removeAllEmptyEntities();
                }
                return newUser;
            } catch (error) {
                logger.error('Failed to create user', { logPriority: LogPriority.HIGH }, error);
                throw error;
            }
        },

        /**
         * Update user details
         * @param userName The username of the user to update
         * @param userDetails The user details to update
         * @returns Promise<IUser>
         * @throws Error if the update fails
         */
        async updateUserDetails(userName: string, userDetails: IUserDetailsData): Promise<IUser> {
            try {
                const updatedUser = await firstValueFrom(usersService.updateUserDetails(userName, userDetails));
                patchState(store, setEntity(updatedUser, { selectId }));
                return updatedUser;
            } catch (error) {
                logger.error('Failed to update user details', { logPriority: LogPriority.HIGH }, error);
                throw error;
            }
        },

        /**
         * Update user company details
         * @param userName The username of the user to update
         * @param userSettings The user settings to update
         * @returns Promise<IUser>
         * @throws Error if the update fails
         */
        async updateUserCompanyDetails(userName: string, userSettings: IUserSettingsData): Promise<IUser> {
            try {
                const updatedUser = await firstValueFrom(usersService.updateUserCompanyDetails(userName, userSettings));
                patchState(store, setEntity(updatedUser, { selectId }));
                return updatedUser;
            } catch (error) {
                logger.error('Failed to update user company details', { logPriority: LogPriority.HIGH }, error);
                throw error;
            }
        },

        /**
         * Send login email to user
         * @param userName The username of the user to send email to
         * @returns Promise<boolean>
         */
        async sendUserLoginEmail(userName: string): Promise<boolean> {
            try {
                await firstValueFrom(usersService.sendUserLoginEmail(userName));
                return true;
            } catch (error) {
                logger.error('Failed to send user login email', { logPriority: LogPriority.HIGH }, error);
                return false;
            }
        },

        /**
         * Select or deselect a user
         * @param userName
         */
        selectUsers(userName: string): void {
            const current = store.selectedUserIds();
            const toggled = xor(current, [userName]);
            patchState(store, { selectedUserIds: toggled });
        },

        /**
         * Clear selected users
         */
        clearSelectedUsers(): void {
            patchState(store, { selectedUserIds: [] });
        },

        /**
         * Sort users
         * @param sort
         */
        sortUsers(sort: Sort): void {
            const sortedUsers = UtilsService.sortData<IUser>(sort, [...store.entities()]);
            patchState(store, { sort }, setEntities(sortedUsers, { selectId }));
        },

        /**
         * Remove all empty entities
         */
        removeAllEmptyEntities(): void {
            patchState(store, removeAllEntities(), initialState);
        },

        /**
         * Check if a user is locked
         * @param user
         * @returns
         */
        isUserLocked(user: IUser): boolean {
            return !!user?.loginInfo && Number(user.loginInfo?.passwordLockout) !== 0;
        },

        /**
         * Check if a user is a Security Admin
         * Security Admins cannot be edited through user management
         * @param user
         * @returns
         */
        isSecurityAdmin(user: IUser): boolean {
            return user?.roles?.some((role) => role.code === AppPermissions.roles.mciSecurityAdmin) ?? false;
        },

        /**
         * Get the normalized (lowercase) status of a user
         * @param user
         * @returns The lowercase status string
         */
        _getNormalizedStatus(user: IUser): string {
            return (user.userInfo.status as string).toLowerCase();
        },

        /**
         * Check if a user is inactive (disabled or expired)
         * @param user
         * @returns True if user is disabled or expired
         */
        _isInactiveUser(user: IUser): boolean {
            const status = this._getNormalizedStatus(user);
            return status === userStatusValue.DISABLED || status === userStatusValue.EXPIRED;
        },

        /**
         * Set the filter for the user list
         * @param status User status to filter by
         */
        setFilter(filter?: IUserFilterParams): void {
            this.clearSelectedUsers();
            patchState(store, { filter: { ...store.filter(), ...filter } });
        },

        /**
         * Clear the user filter
         */
        emptyFilter(): void {
            this.clearSelectedUsers();
            patchState(store, { filter: {} });
        },

        /**
         * Get user by username
         * @param userName The username to search for
         * @returns The user if found, otherwise null
         */
        getUserByUserName(userName: string): IUser | null {
            if (store.entityMap()[userName]) {
                return store.entityMap()[userName];
            }
            return null;
        },

        /**
         * Execute bulk action on selected users
         * @param action The action to perform on selected users
         * @returns Promise<boolean>
         */
        async bulkAction(action: UserBulkAction): Promise<boolean> {
            const allSelectedUsers = store.allSelectedUsers();
            if (allSelectedUsers.length === 0) {
                return false;
            }

            if (action === userBulkAction.ENABLED) {
                const inactiveUsers = allSelectedUsers.filter(
                    (user) => !this.isSecurityAdmin(user) && this._isInactiveUser(user),
                );
                const inactiveUsersWithoutRoles = this._getUsersWithoutRoles(inactiveUsers);
                if (inactiveUsersWithoutRoles.length > 0) {
                    ngrxStore.dispatch(
                        showErrorMessage({
                            message:
                                'Users must have at least one role assigned before they can be enabled. Please assign roles first.',
                            title: 'ERROR',
                        }),
                    );
                    return false;
                }
            }

            const eligibleUsers = this._getEligibleUsersForAction(allSelectedUsers, action);

            if (eligibleUsers.length === 0) {
                return false;
            }

            const userNames = eligibleUsers.map((user) => user.userInfo.userName);
            const userCount = userNames.length;

            try {
                await firstValueFrom(usersService.bulkAction(action, userNames));

                this._applyLocalStateChanges(action, userNames);
                this.clearSelectedUsers();

                const successMessage = this._getSuccessMessage(action, userCount);
                ngrxStore.dispatch(showSuccessMessage({ message: successMessage, title: 'Success' }));

                return true;
            } catch (error) {
                logger.error(`Bulk action '${action}' failed`, { logPriority: LogPriority.HIGH }, error);

                const errorMessage = this._getErrorMessage(action, userCount);
                ngrxStore.dispatch(
                    showErrorMessage({
                        message: errorMessage,
                        title: 'ERROR',
                    }),
                );

                return false;
            }
        },

        /**
         * Get success message for bulk action
         * @param action The action that was performed
         * @param userCount Number of users affected
         * @returns Success message string
         */
        _getSuccessMessage(action: UserBulkAction, userCount: number): string {
            const s = userCount !== 1 ? 's' : '';
            switch (action) {
                case userBulkAction.EMAILED:
                    return `Login email${s} ha${s ? 've' : 's'} been sent.`;
                case userBulkAction.ENABLED:
                    return `User${s} ha${s ? 've' : 's'} been enabled.`;
                case userBulkAction.DISABLED:
                    return `User${s} ha${s ? 've' : 's'} been disabled.`;
                case userBulkAction.UNLOCKED:
                    return `User${s} ha${s ? 've' : 's'} been unlocked.`;
                default:
                    return 'Action completed successfully.';
            }
        },

        /**
         * Get error message for bulk action
         * @param action The action that failed
         * @param userCount Number of users affected
         * @returns Error message string
         */
        _getErrorMessage(action: UserBulkAction, userCount: number): string {
            const s = userCount !== 1 ? 's' : '';
            switch (action) {
                case userBulkAction.EMAILED:
                    return `Failed to send login email${s}. Please try again later.`;
                case userBulkAction.ENABLED:
                    return `Failed to enable user${s}. Please try again later.`;
                case userBulkAction.DISABLED:
                    return `Failed to disable user${s}. Please try again later.`;
                case userBulkAction.UNLOCKED:
                    return `Failed to unlock user${s}. Please try again later.`;
                default:
                    return 'Action failed. Please try again later.';
            }
        },

        /**
         * Get users eligible for a specific bulk action
         * Security Admins are excluded from all bulk actions
         * @param users The users to filter
         * @param action The action to filter by
         * @returns An array of users eligible for the action
         */
        _getEligibleUsersForAction(users: IUser[], action: UserBulkAction): IUser[] {
            const nonSecurityAdminUsers = users.filter((user) => !this.isSecurityAdmin(user));

            switch (action) {
                case userBulkAction.DISABLED:
                    return nonSecurityAdminUsers.filter(
                        (user) => this._getNormalizedStatus(user) === userStatusValue.ENABLED,
                    );
                case userBulkAction.ENABLED:
                    return nonSecurityAdminUsers.filter(
                        (user) => this._isInactiveUser(user) && user.roles && user.roles.length > 0,
                    );
                case userBulkAction.UNLOCKED:
                    return nonSecurityAdminUsers.filter((user) => this.isUserLocked(user));
                case userBulkAction.EMAILED:
                    return nonSecurityAdminUsers;
                default:
                    return [];
            }
        },

        /**
         * Get users that don't have any roles assigned
         * @param users The users to filter
         * @returns An array of users without roles
         */
        _getUsersWithoutRoles(users: IUser[]): IUser[] {
            return users.filter((user) => !user.roles || user.roles.length === 0);
        },

        /**
         * Apply local state changes after successful bulk action
         * @param action The action that was performed
         * @param userNames The usernames that were affected
         */
        _applyLocalStateChanges(action: UserBulkAction, userNames: string[]): void {
            switch (action) {
                case userBulkAction.DISABLED:
                    this._updateStatus(userNames, 'Disabled');
                    break;
                case userBulkAction.ENABLED:
                    this._updateStatus(userNames, 'Enabled');
                    break;
                case userBulkAction.UNLOCKED:
                    this._unlockUsers(userNames);
                    break;
            }
        },

        /**
         * Update the status of multiple users
         * @param userNames Array of usernames to update
         * @param status New status to apply to the users
         */
        _updateStatus(userNames: string[], status: UserStatus): void {
            const users = userNames.map((userName) => {
                const user = store.entityMap()[userName];
                return {
                    ...user,
                    userInfo: {
                        ...user.userInfo,
                        status,
                    },
                } as IUser;
            });
            patchState(store, setEntities(users, { selectId }));
        },

        /**
         * Unlock multiple users
         * @param userNames Array of usernames to unlock
         */
        _unlockUsers(userNames: string[]): void {
            const users: IUser[] = userNames.map((userName) => {
                const user = store.entityMap()[userName];
                return {
                    ...user,
                    loginInfo: {
                        ...user.loginInfo,
                        passwordLockout: '0',
                    },
                } as IUser;
            });
            patchState(store, setEntities(users, { selectId }));
        },
    })),
);
